import React, { useState, createRef, useEffect, forwardRef } from "react";
import {
  StyleSheet,
  View,
  Platform,
  KeyboardAvoidingView,
  ScrollView,
  Dimensions,
  StatusBar,
} from "react-native";
import { Text, InfoBox, InputAccessoryNavigation } from "../../components";
import { UserFields, InfoBoxType, InfoBoxIconType, DropdownType } from "../../enums";

import { ThemeType } from "../../theme/theme";
import { Stack } from "../../components/layout/stack";
import { translate } from "../../i18n/translate";
import { useTheme } from "react-native-paper";
import { useIsMounted } from "../../hooks/useIsMounted";
import { useNavigation } from "@react-navigation/native";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { signupSchema } from "../home/schemas";
import { Inline } from "../../components/layout/inline";
import { UserService } from "../../api/UserService";
import { formatPhoneString, formatCognitoPhoneNumber } from "../../utils/phoneNumber";
import { testProps } from "../../utils/testProps";
import { TextInput, theme as themeUiComponent, Button, typography } from "ui-components";
//select-property start
import { Dropdown } from "../../components/dropdown/dropdown";
import moment from "moment";
import { PropertyService } from "../../api/PropertyService";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";
import { domusoResponsiveStyleHelper } from "../new/utils/domuso-responsive";
import { DomusoIcon } from "../../../assets/domuso-icon";
//select-property end
import { Grid, Container, Row, Column } from "ui-grid";
import { Footer } from "../../components/new/footer/footer";
import { MaterialIcons } from "@expo/vector-icons";

interface SignUpScreenProps {
  route: { params: any };
}
const DEBOUNCE_TIME = 500; //ms

export const SignUpScreen = ({ route }: SignUpScreenProps) => {
  const theme = useTheme();
  const styles = getStyles(theme as ThemeType);
  const isMounted = useIsMounted();
  const navigation = useNavigation();
  const [errorMsg, setErrorMsg] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const { params } = route;
  const inputAccessoryViewID = "inputAccessoryViewID1";
  const ref = [createRef(), createRef(), createRef(), createRef()];
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  const [phoneString, setPhoneString] = useState();

  //select-property start
  const logger = useSelector((state: RootState) => state.logger);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [isNoPropsFound, setIsNoPropsFound] = useState<any>(false);
  const [isSearchLoading, setSearchLoading] = useState(false);
  const [lookupTime, setLookupTime] = useState(moment().valueOf());
  const [delayedSearchTimeout, setDelayedSearchTimeout] = useState<any>();
  //select-property end

  if (!isMounted) {
    return null;
  }
  const {
    register,
    trigger,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(signupSchema),
  });

  const handlePhoneFieldUpdate = (field: string, val: string) => {
    const formatted = formatPhoneString(val);
    const numberVal = formatCognitoPhoneNumber(val);
    setPhoneString(formatted);
    clearErrors(field);
    setValue(field, numberVal, { shouldValidate: true });
  };

  const handleFieldUpdate = (field: string, val: string) => {
    clearErrors(field);
    setValue(field, val, { shouldValidate: true });
  };

  useEffect(() => {
    register(UserFields.PROP_ID);
    register(UserFields.FIRST_NAME);
    register(UserFields.LAST_NAME);
    register(UserFields.EMAIL);
    register(UserFields.PHONE);
  }, [register]);

  const onContinueHandler = async () => {
    const { first_name, last_name, email, phone, prop_id } = getValues();
    try {
      setLoading(true);
      setErrorMsg(null);
      await UserService.signUp({ first_name, last_name, email, phone, prop_id });
      setLoading(false);
      navigation.navigate("SignUpConfirmed", {
        ...params,
        first_name,
        last_name,
        email,
        phone,
      });
    } catch (error) {
      let msg = "Something went wrong. Please verify that your email address is correct.";
      setErrorMsg(msg);
      setLoading(false);
    }
  };

  const renderErrorBox = () => {
    if (!errorMsg) return null;
    return (
      <InfoBox
        infoBoxType={InfoBoxType.RED}
        headlineIconType={InfoBoxIconType.RED_X}
        headline={translate("authStack.signupError")}>
        <Text>{errorMsg}</Text>
      </InfoBox>
    );
  };

  //select-property start
  const onPropertySelectHandler = async (prop: any) => {
    try {
      const propertySelected =
        prop && prop.code ? { ...prop, name: prop.label, code: prop.code } : null;
      setSelectedProperty(propertySelected);
      let property = await PropertyService.getPublicPropInfo(propertySelected.code);
      clearErrors(UserFields.PROP_ID);
      setValue(UserFields.PROP_ID, property?.id, { shouldValidate: true });
    } catch (error) {
      // console.log("onPropertySelectHandler error: ", error);
    }
  };

  const onPropertySearchHandler = async (value: any) => {
    if (selectedProperty) setSelectedProperty(null);
    if (value.length <= 1) {
      setAvailableProperties([]);
      setIsNoPropsFound(false);
      return;
    }
    const timeValue = moment().valueOf();

    const doPropertySearch = async () => {
      setLookupTime(timeValue);
      try {
        setSearchLoading(true);
        const res = await PropertyService.searchPublicProperties(value);
        let props = res.map((prop: any) => ({ value: prop.code, ...prop }));
        props = props.filter((prop) => prop.allow_self_signup);
        setAvailableProperties(props);
        setIsNoPropsFound(!Boolean(props.length));
      } catch (error) {
        await logger.log({ error });
      }
      setSearchLoading(false);
    };

    if (timeValue - lookupTime > DEBOUNCE_TIME) {
      doPropertySearch();
    } else {
      clearTimeout(delayedSearchTimeout);
      let timeout = setTimeout(doPropertySearch, DEBOUNCE_TIME);
      setDelayedSearchTimeout(timeout);
    }
  };
  //select-property end

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      keyboardVerticalOffset={
        Platform.OS === "web"
          ? undefined
          : Platform.OS == "android"
          ? Dimensions.get("window").height - StatusBar.currentHeight
          : 120
      }>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollableArea}
        keyboardShouldPersistTaps={"handled"}>
        <StatusBar
          hidden={false}
          translucent={false}
          backgroundColor={themeUiComponent.colors.white}
          barStyle={"dark-content"}
        />
        <View style={styles.inner}>
          <Grid>
            <Container>
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2.75, lg: 3.75 }} />
                <Column span={{ xs: 12, sm: 8, md: 6.5, lg: 4.5 }}>
                  <View style={{ marginTop: errorMsg ? 320 : 255 }}>
                    <Stack space="smaller">
                      <InputRow
                        formName={UserFields.FIRST_NAME}
                        label="authStack.account.firstName"
                        inputAccessoryViewID={inputAccessoryViewID}
                        ref={ref[0] as any}
                        errors={errors}
                        styles={styles}
                        handleFieldUpdate={handleFieldUpdate}
                        onFocus={() => setCurrentInputIndex(0)}
                        onSubmitEditing={() => (ref[1].current as any).focus()}
                        returnKeyType="next"
                      />
                      <InputRow
                        formName={UserFields.LAST_NAME}
                        label="authStack.account.lastName"
                        inputAccessoryViewID={inputAccessoryViewID}
                        ref={ref[1] as any}
                        errors={errors}
                        styles={styles}
                        handleFieldUpdate={handleFieldUpdate}
                        onFocus={() => setCurrentInputIndex(1)}
                        onSubmitEditing={() => (ref[2].current as any).focus()}
                        returnKeyType="next"
                      />
                      <InputRow
                        formName={UserFields.EMAIL}
                        type="email-address"
                        label="authStack.account.email"
                        inputAccessoryViewID={inputAccessoryViewID}
                        ref={ref[2] as any}
                        errors={errors}
                        styles={styles}
                        handleFieldUpdate={handleFieldUpdate}
                        onFocus={() => setCurrentInputIndex(2)}
                        onSubmitEditing={() => (ref[3].current as any).focus()}
                        returnKeyType="next"
                      />
                      <InputRow
                        formName={UserFields.PHONE}
                        type="phone-pad"
                        label="authStack.account.phone"
                        inputAccessoryViewID={inputAccessoryViewID}
                        errors={errors}
                        styles={styles}
                        value={phoneString}
                        handleFieldUpdate={handlePhoneFieldUpdate}
                        ref={ref[3] as any}
                        onFocus={() => setCurrentInputIndex(3)}
                        returnKeyType="go"
                        onSubmitEditing={isValid && handleSubmit(onContinueHandler)}
                      />
                    </Stack>
                    <Stack space="medium">
                      <Button
                        text={
                          (
                            <View style={styles.buttonTextContainer}>
                              <Text style={styles.appButtonText}>
                                {translate("componentStatic.nextConfirmEmail")}{" "}
                              </Text>
                              <MaterialIcons
                                name="arrow-right-alt"
                                size={28}
                                color={themeUiComponent.colors.white}
                                style={{ marginTop: 3 }}
                              />
                            </View>
                          ) as any
                        }
                        onPress={handleSubmit(onContinueHandler)}
                        loading={isLoading}
                        disabled={!isValid || isLoading}
                        mode="contained"
                        style={styles.button}
                        textStyle={{ fontFamily: "Lato_700Bold" }}
                        {...testProps("signup-button")}
                      />
                      <View style={styles.loginLinkContainer}>
                        <Text tx="authStack.alreadyHaveAccount" style={styles.text} />
                        <Text
                          tx="authStack.login"
                          style={[styles.text, styles.link]}
                          onPress={() => navigation.navigate("SignIn")}
                        />
                      </View>
                      {Platform.OS === "ios" ? (
                        <InputAccessoryNavigation
                          inputAccessoryViewID={inputAccessoryViewID}
                          ref={ref as any}
                          currentInputIndex={currentInputIndex}
                        />
                      ) : null}
                    </Stack>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2.75, lg: 3.75 }} />
              </Row>
            </Container>
          </Grid>
          <View style={styles.headerContainer}>
            <Grid>
              <Container>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2.75, lg: 3.75 }} />
                  <Column span={{ xs: 12, sm: 8, md: 6.5, lg: 4.5 }}>
                    <View style={{ marginVertical: 15 }}>{renderErrorBox()}</View>
                    <Stack space="small">
                      <>
                        <View style={{ alignItems: "center" }}>
                          <DomusoIcon
                            height={45}
                            width={45}
                            color={themeUiComponent.colors.primary}
                          />
                          <View style={{ marginTop: 10, marginBottom: 20 }}>
                            <Text style={styles.loginHeading} text="Create Account" />
                          </View>
                        </View>
                        <View style={{ zIndex: 10 }}>
                          <Dropdown
                            onChangeText={onPropertySearchHandler}
                            onSelectHandler={onPropertySelectHandler}
                            value={selectedProperty}
                            isLoading={isSearchLoading}
                            placeholder={translate("authStack.typePropertyName")}
                            items={availableProperties}
                            formName={UserFields.PROP_ID}
                            mode={DropdownType.DROPDOWN}
                            errors={errors}
                            useUiComponentTextInput
                            useTouchableHighlightFromReactNative
                            textInputLabel={translate("authStack.searchAProperty")}
                            dropdownSelectorContainerStyle={[
                              styles.dropdownSelectorContainerStyle,
                              {
                                top: errors[UserFields.PROP_ID] ? "78%" : "94%",
                              },
                            ]}
                          />
                        </View>
                      </>
                    </Stack>
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2.75, lg: 3.75 }} />
                </Row>
              </Container>
            </Grid>
          </View>
        </View>
      </ScrollView>
      <Footer />
    </KeyboardAvoidingView>
  );
};

const getStyles = (theme: ThemeType) => {
  const { colors, fontSize, spacing } = theme;
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: themeUiComponent.colors.white,
    },
    scrollView: { flexGrow: 0 },
    scrollableArea: {},
    inner: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {},
        webDesktop: {},
        iosPhone: {},
        iosTablet: {},
        androidPhone: {},
        androidTablet: {},
      }),
    },
    propertyContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      paddingVertical: spacing.medium,
      paddingHorizontal: spacing.medium,
      marginTop: spacing.large,
      marginBottom: spacing.medium,
      backgroundColor: colors.white,
      shadowColor: colors.text,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.3,
      shadowRadius: 4,
      elevation: 5,
    },
    text: {
      fontSize: fontSize.large,
      color: colors.text,
    },
    label: {
      fontSize: fontSize.large,
      marginLeft: spacing.small,
    },
    button: {
      marginTop: spacing.medium,
      paddingVertical: Platform.OS == "ios" ? 10 : 13,
    },
    flexCenter: {
      display: "flex",
      alignItems: "center",
    },
    loginLinkContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      paddingBottom: spacing.small,
    },
    link: {
      marginLeft: 4,
      fontSize: fontSize.large,
      textDecorationLine: "underline",
    },
    loginHeading: {
      fontSize: fontSize.largerPlus,
      fontWeight: "bold",
    },
    subHeading: {
      fontSize: 18,
      textAlign: "center",
    },
    headerContainer: {
      position: "absolute",
      top: 0,
      width: "100%",
      alignSelf: "center",
      zIndex: 10,
    },
    dropdownSelectorContainerStyle: {
      borderRadius: 10,
      borderWidth: 0.3,
    },
    appButtonText: {
      ...typography.title1,
      color: themeUiComponent.colors.white,
      textAlign: "center",
      fontFamily: "Lato_700Bold",
    },
    buttonTextContainer: {
      paddingTop: Platform.OS == "ios" ? 5 : 0,
      flexDirection: "row",
      alignSelf: "center",
    },
  });
};

const InputRow = forwardRef(
  (
    {
      formName,
      label,
      type = "default",
      onFocus,
      errors,
      inputAccessoryViewID,
      handleFieldUpdate,
      styles,
      value,
      returnKeyType,
      onSubmitEditing,
    }: any,
    ref: any
  ) => (
    <TextInput
      keyboardType={type}
      accessibilityStates
      value={value}
      onChangeText={(val) => handleFieldUpdate(formName, val)}
      returnKeyType={returnKeyType}
      inputAccessoryViewID={inputAccessoryViewID}
      ref={ref}
      onFocus={onFocus}
      onSubmitEditing={onSubmitEditing}
      error={errors[formName]}
      label={translate(label) as string}
      // placeholderStyle={{ flex: 0 }}
      {...testProps(formName)}
    />
  )
);
